import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useStore from '../../store/useStore';
import useSchoolId from '../../hooks/useSchoolId';
import * as db from '../../db';
import { EdEaseMark } from '../brand/EdEaseLogo';
import { NAV } from '../../utils/sidebarNav';

// Platform admin only: the company's own pages, above the menu of the school being viewed.
const PLATFORM_NAV = [
  { id: 'platform',          label: 'Overview',       icon: 'ti-chart-pie' },
  { id: 'platform-schools',  label: 'Schools',        icon: 'ti-building-community' },
  { id: 'platform-payments', label: 'Payments',       icon: 'ti-file-invoice' },
  { id: 'platform-onboard',  label: 'Onboard school', icon: 'ti-circle-plus' },
  { id: 'platform-demo',     label: 'Demo access',    icon: 'ti-key' },
  { id: 'platform-leads',    label: 'Leads',          icon: 'ti-users-group' },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { page, setPage, hasPermission, user } = useStore();
  const isPlatformAdmin = !!user?.isPlatformAdmin;
  const schoolId = useSchoolId();
  const canSeeFees = hasPermission('fees.view');
  const [pendingCount, setPendingCount] = useState(null);
  // Platform pages stay minimized until the header is clicked (or you land on one of them)
  const [platformOpen, setPlatformOpen] = useState(false);
  useEffect(() => { if (String(page).startsWith('platform')) setPlatformOpen(true); }, [page]);

  // Families with unpaid fees, straight from Supabase. Refreshed every 5 minutes and when the window
  // regains focus; on a failed refresh the last good number stays on screen.
  useEffect(() => {
    setPendingCount(null);
    if (!schoolId || !canSeeFees) return undefined;
    let cancelled = false;
    let lastLoad = 0;
    const load = (fromFocus) => {
      // coming back to the tab refreshes the badge at most once a minute
      if (fromFocus === true && Date.now() - lastLoad < 60 * 1000) return;
      lastLoad = Date.now();
      db.getFeePendingCount(schoolId)
        .then(n => { if (!cancelled) setPendingCount(n); })
        .catch(err => console.error('Fee pending count failed', err));
    };
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 5 * 60 * 1000);
    const onFocus = () => load(true);
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [schoolId, canSeeFees]);

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      {/* Logo — same height as the Topbar so the two bottom borders line up */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <EdEaseMark size={24} variant="white" />
        </div>
        <div className="sidebar-logo-text">
          <div className="sidebar-logo-name">EdEase</div>
          <div className="sidebar-logo-tag">School ERP Pro</div>
        </div>
      </div>

      {/* Nav items */}
      <div className="sidebar-nav">
        {isPlatformAdmin && (
          <div>
            <div
              className="sidebar-item"
              role="button"
              tabIndex={0}
              aria-expanded={platformOpen}
              title="Platform"
              style={{ marginTop: 6 }}
              onClick={() => setPlatformOpen(o => !o)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPlatformOpen(o => !o); } }}
            >
              <i className="ti ti-layout-dashboard sidebar-item-icon" aria-hidden="true" />
              <span className="sidebar-item-label" style={{ flex: 1 }}>Platform</span>
              {!collapsed && <i className={`ti ${platformOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} aria-hidden="true" style={{ fontSize: 14, opacity: 0.7 }} />}
            </div>
            {platformOpen && PLATFORM_NAV.map(item => (
              <div
                key={item.id}
                className={`sidebar-item${page === item.id ? ' active' : ''}`}
                onClick={() => setPage(item.id)}
                title={collapsed ? item.label : undefined}
              >
                <i className={`ti ${item.icon} sidebar-item-icon`} aria-hidden="true" />
                <span className="sidebar-item-label">{item.label}</span>
              </div>
            ))}
            <div className="sidebar-divider" style={{ marginTop: 2 }} />
          </div>
        )}
        {NAV.map((group, gi) => {
          const visibleItems = group.items.filter(item => hasPermission(item.perm));
          if (visibleItems.length === 0) return null;
          return (
            <div key={gi}>
              {gi > 0 && <div className="sidebar-divider" style={{ marginTop: 2 }} />}
              {!collapsed && <div className="sidebar-section" style={gi === 0 ? { marginTop: 6 } : undefined}>{group.section}</div>}
              {visibleItems.map(item => (
                <div
                  key={item.id}
                  className={`sidebar-item${page === item.id ? ' active' : ''}`}
                  onClick={() => setPage(item.id)}
                  title={collapsed ? item.label : undefined}
                >
                  <i className={`ti ${item.icon} sidebar-item-icon`} aria-hidden="true" />
                  <span className="sidebar-item-label">{item.label}</span>
                  {item.badge && canSeeFees && pendingCount > 0 && (
                    <span className="sidebar-item-badge" title={`${pendingCount} ${pendingCount === 1 ? 'family' : 'families'} with unpaid fees`}>
                      {pendingCount > 999 ? '999+' : String(pendingCount)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Collapse control: a pill on the sidebar's right edge, positioned in CSS */}
      <button
        type="button"
        className="sidebar-toggle"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
      </button>
    </aside>
  );
}
